import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FileClock } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectIsOrgOwnerOrAdmin } from "../../authentication/authenticationSlice";
import { selectActivePaymentTypes } from "../../payment-types/payment-types-slice";
import { fetchPaymentTypes } from "../../payment-types/slice-extensions/payment-types-slice-extension";
import { policySentence } from "../../portal/utils/policySentence";
import { fetchCancellationPolicy, saveCancellationPolicy } from "../slice-extensions/cancellation-policy-slice-extension";
import { previewInput, toValidRequest } from "../utils/policyPreview";
import CancellationPolicyFields, { CancellationPolicyValues, toPolicyValues } from "./CancellationPolicyFields";

/**
 * Organization settings card: the default cancellation policy every Session uses
 * unless it sets its own. Owner and Admin edit; an Instructor reads.
 */
const CancellationPolicyCard = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const canEdit = useAppSelector(selectIsOrgOwnerOrAdmin);
  const paymentTypes = useAppSelector(selectActivePaymentTypes);
  const [values, setValues] = useState<CancellationPolicyValues | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchPaymentTypes());
    dispatch(fetchCancellationPolicy())
      .unwrap()
      .then((policy) => setValues(toPolicyValues(policy)))
      .catch((rejected: { message?: string }) => setError(rejected?.message ?? null));
  }, [dispatch]);

  const handleSave = async () => {
    if (!values) return;
    const request = toValidRequest(values);
    if (!request) {
      setError(t("settings.cancellationPolicy.feeRequired"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await dispatch(saveCancellationPolicy(request)).unwrap();
      setValues(toPolicyValues(saved));
      toast.success(t("settings.cancellationPolicy.saved"));
    } catch (rejected) {
      setError((rejected as { message?: string })?.message ?? null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
          <FileClock size={20} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold leading-tight">{t("settings.cancellationPolicy.title")}</h2>
          <p className="mt-0.5 text-sm text-grey-50 dark:text-grey-70">{t("settings.cancellationPolicy.intro")}</p>
        </div>
      </div>

      {values && (
        <>
          <CancellationPolicyFields
            idPrefix="org_cancellation"
            values={values}
            paymentTypes={paymentTypes}
            disabled={!canEdit || saving}
            onChange={setValues}
          />
          <div className="mt-4 rounded-2xl bg-grey-95 p-3 text-sm text-grey-20 dark:bg-primary-20 dark:text-white">
            <span className="font-medium">{t("settings.cancellationPolicy.preview")}: </span>
            {policySentence(previewInput(values, paymentTypes), t, i18n.language)}
          </div>
          {canEdit ? (
            <div className="mt-4 flex justify-end">
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? t("common.saving") : t("settings.cancellationPolicy.save")}
              </button>
            </div>
          ) : (
            <p className="mt-3 text-xs text-grey-50 dark:text-grey-70">{t("settings.cancellationPolicy.readOnly")}</p>
          )}
        </>
      )}

      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
};

export default CancellationPolicyCard;
